import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileText, Loader2, PenLine, RotateCcw, ShieldCheck, Users } from 'lucide-react';

type SignerStatus = {
  id: string;
  signer_name: string;
  signer_email: string;
  role_label?: string | null;
  status: 'pending' | 'signed';
  signed_at?: string | null;
};

type TokenData = {
  document_title: string;
  file_name: string;
  signer_name: string;
  signer_email: string;
  role_label?: string | null;
  status: 'pending' | 'signed';
  signed_at?: string | null;
  signers: SignerStatus[];
};

function formatDate(value?: string | null) {
  if (!value) return '';
  return new Intl.DateTimeFormat('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function extractToken() {
  return window.location.pathname.split('/loi-sign/')[1]?.split('/')[0] || '';
}

export default function LoiSignaturePage() {
  const token = extractToken();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [signerName, setSignerName] = useState('');
  const [typedSignature, setTypedSignature] = useState('');
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const [drawing, setDrawing] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [success, setSuccess] = useState(false);

  const documentUrl = `/api/public/loi-sign/${token}/document`;
  const signedDocumentUrl = `/api/public/loi-sign/${token}/signed-document`;

  useEffect(() => {
    async function loadToken() {
      try {
        if (!token) throw new Error('Lien de signature invalide.');
        const response = await fetch(`/api/public/loi-sign/${token}/validate`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Lien de signature invalide.');
        setTokenData(data);
        setSignerName(data.signer_name || '');
        setTypedSignature(data.signer_name || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de validation.');
      } finally {
        setLoading(false);
      }
    }
    loadToken();
  }, [token]);

  const getCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const beginDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(event);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.lineWidth = 3;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#0f172a';
    context.beginPath();
    context.moveTo(point.x, point.y);
    setDrawing(true);
    setHasDrawing(true);
  };

  const draw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(event);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const clearDrawing = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
  };

  const typedSignatureDataUrl = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 900;
    canvas.height = 260;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.fillStyle = '#0f172a';
    context.font = 'italic 76px Georgia, "Times New Roman", serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText((typedSignature || signerName).trim(), canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL('image/png');
  };

  const submitSignature = async () => {
    try {
      setSaving(true);
      setError(null);
      if (!signerName.trim()) throw new Error('Inscris ton nom complet avant de signer.');

      let signatureImage: string | null = null;
      if (mode === 'draw') {
        if (!hasDrawing) throw new Error('Dessine ta signature ou utilise le mode nom tapé.');
        signatureImage = canvasRef.current?.toDataURL('image/png') || null;
      } else {
        if (!typedSignature.trim()) throw new Error('Tape ton nom pour générer la signature.');
        signatureImage = typedSignatureDataUrl();
      }
      if (!signatureImage) throw new Error('Signature invalide.');

      const response = await fetch(`/api/public/loi-sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: signerName.trim(),
          signature_data: {
            method: mode,
            typedName: mode === 'type' ? typedSignature.trim() : null,
            signerName: signerName.trim(),
            timestamp: new Date().toISOString(),
          },
          signature_image: signatureImage.split(',').pop(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Erreur pendant la signature.');
      setTokenData((current) => current ? { ...current, status: 'signed', signers: data.signers || current.signers } : current);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur pendant la signature.');
    } finally {
      setSaving(false);
    }
  };

  const signedCount = tokenData?.signers.filter((s) => s.status === 'signed').length || 0;
  const totalCount = tokenData?.signers.length || 0;

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-700">
          <Loader2 className="h-5 w-5 animate-spin" />
          Validation du lien de signature...
        </div>
      </main>
    );
  }

  if (error && !tokenData) {
    return (
      <main className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <section className="max-w-lg rounded-lg bg-white p-8 shadow-sm border border-slate-200">
          <h1 className="text-xl font-semibold text-slate-950">Lien non disponible</h1>
          <p className="mt-3 text-slate-600">{error}</p>
        </section>
      </main>
    );
  }

  if (success || tokenData?.status === 'signed') {
    return (
      <main className="min-h-screen bg-slate-100 px-4 py-8">
        <section className="mx-auto max-w-3xl rounded-lg bg-white border border-slate-200 shadow-sm p-8">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-emerald-100 p-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-950">Signature enregistrée</h1>
              <p className="mt-2 text-slate-600">
                Merci. Ta signature est maintenant inscrite au registre central du document.
              </p>
            </div>
          </div>
          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-900">{tokenData?.document_title}</div>
            <div className="mt-1 text-sm text-slate-600">{signedCount} / {totalCount} signatures reçues.</div>
          </div>
          <a
            href={signedDocumentUrl}
            className="mt-6 inline-flex items-center rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Télécharger le PDF avec signatures reçues
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-6">
      <section className="mx-auto max-w-6xl">
        <div className="mb-4 rounded-lg bg-white border border-slate-200 p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-700">
                <ShieldCheck className="h-4 w-4" />
                PRIsME Master - signature centralisée
              </div>
              <h1 className="mt-2 text-2xl font-semibold text-slate-950">{tokenData?.document_title}</h1>
              <p className="mt-1 text-sm text-slate-600">Signataire: {tokenData?.signer_name} ({tokenData?.signer_email})</p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs uppercase text-slate-500">Progression</div>
              <div className="text-lg font-semibold text-slate-950">{signedCount} / {totalCount}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
          <div className="rounded-lg bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
              <FileText className="h-4 w-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-900">{tokenData?.file_name}</span>
            </div>
            <iframe title="Document a signer" src={documentUrl} className="h-[72vh] w-full bg-slate-50" />
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg bg-white border border-slate-200 shadow-sm p-5">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-950">
                <PenLine className="h-5 w-5" />
                Signature
              </h2>
              <label className="mt-4 block text-sm font-medium text-slate-700">Nom complet</label>
              <input
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
              />

              <div className="mt-4 grid grid-cols-2 rounded-md border border-slate-200 p-1">
                <button onClick={() => setMode('draw')} className={`rounded px-3 py-2 text-sm font-semibold ${mode === 'draw' ? 'bg-slate-950 text-white' : 'text-slate-600'}`}>
                  Dessiner
                </button>
                <button onClick={() => setMode('type')} className={`rounded px-3 py-2 text-sm font-semibold ${mode === 'type' ? 'bg-slate-950 text-white' : 'text-slate-600'}`}>
                  Nom tapé
                </button>
              </div>

              {mode === 'draw' ? (
                <div className="mt-4">
                  <canvas
                    ref={canvasRef}
                    width={900}
                    height={260}
                    onPointerDown={beginDrawing}
                    onPointerMove={draw}
                    onPointerUp={() => setDrawing(false)}
                    onPointerLeave={() => setDrawing(false)}
                    className="h-40 w-full cursor-crosshair rounded-md border border-slate-300 bg-white touch-none"
                  />
                  <button onClick={clearDrawing} className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-950">
                    <RotateCcw className="h-4 w-4" />
                    Effacer
                  </button>
                </div>
              ) : (
                <div className="mt-4">
                  <input
                    value={typedSignature}
                    onChange={(event) => setTypedSignature(event.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                  <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-center font-serif text-3xl italic text-slate-950">
                    {typedSignature || signerName || 'Signature'}
                  </div>
                </div>
              )}

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
              <button
                onClick={submitSignature}
                disabled={saving}
                className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-emerald-700 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Signer le document
              </button>
            </div>

            <div className="rounded-lg bg-white border border-slate-200 shadow-sm p-5">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-950">
                <Users className="h-5 w-5" />
                Suivi des signatures
              </h2>
              <div className="mt-4 space-y-3">
                {tokenData?.signers.map((signer) => (
                  <div key={signer.id} className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{signer.signer_name}</div>
                      <div className="text-xs text-slate-500">{signer.signer_email}</div>
                      {signer.signed_at ? <div className="mt-1 text-xs text-slate-500">{formatDate(signer.signed_at)}</div> : null}
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${signer.status === 'signed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {signer.status === 'signed' ? 'Signé' : 'En attente'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
